import React from 'react';
import classnames from 'classnames';
import { Location } from 'history';
import { FormattedMessage } from 'react-intl';
import Styled from 'styled';
import PropTypes from 'prop-types';
import Helmet from 'helmet/helmet';

import { Halfpage, ZoznamHalfpageTop } from 'lib/google/dfp/ad-units/halfpage';

import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import { Site } from 'four-nets/site';
import Avatar from 'four-nets/avatar/avatar';
import Cover from 'four-nets/photoblog/user-blog/cover/cover';
import { displayedName } from 'four-nets/user/utils';

import AuthorName from './author-side-components/author-name/author-name';
import AuthorProject from './author-side-components/author-project/author-project';
import AuthorActivity from './author-side-components/author-activity/author-activity';
import UserInformation from './author-side-components/user-information/user-information';
import UserSummaryInfo from './author-side-components/user-summary-info/user-summary-info';
import AuthorCollections from './author-side-components/author-collections/author-side-collections';
import VendorInformation from './author-side-components/vendor-information/vendor-information';
import AuthorSocialStats from './author-side-components/author-social-stats/author-social-stats';
import VendorSummaryInfo from './author-side-components/vendor-summary-info/vendor-summary-info';
import AuthorLikedPhotoblogs from './author-side-components/author-liked-photoblogs/author-liked-photoblogs';

import { CoverFragment } from '../cover/cover.graphql';
import { AuthorSideFragment } from './author-side.graphql';
import { AuthorVendorFragment } from './author-vendor.graphql';

import defaultStyles from './author-side.scss';

export interface Props {
    styles: typeof defaultStyles;
    location: Location<any>;
    onMoreGroups?: () => void;
    onMorePhotoBlogs?: () => void;
    writeMail: (to: string) => void;
    vendor?: AuthorVendorFragment;
    user: AuthorSideFragment;
    userBlog: CoverFragment;
}

interface State {
    openLikers: boolean;
    openFriends: boolean;
    expanded: boolean;
}

class AuthorSide extends React.PureComponent<Props, State> {
    static contextTypes = {
        origin: PropTypes.string,
        site: PropTypes.instanceOf(Site),
    };

    state = {
        openLikers: false,
        openFriends: false,
        expanded: false,
    };

    renderHelmet = (H: any) => {
        const { origin, site } = this.context;
        const { location, user, vendor } = this.props;

        return (
            <FormattedMessage
                id='USERS_PHOTOBLOG'
                values={{ gender: user && user.profile && user.profile.sex === 1 ? 'f' : 'm' }}
            >
                {message => {
                    let title = `${message} ${displayedName(user)}`;
                    let description = user.profile && user.profile.aboutMe;

                    if (vendor && vendor.name) {
                        title = vendor.name;
                    }

                    if (vendor && (vendor.cardText || vendor.about)) {
                        description = vendor.cardText || vendor.about;
                    }

                    return (
                        <React.Fragment>
                            <H>
                                <title>
                                    {title} | {site.name}
                                </title>
                            </H>
                            {user!.profile ? (
                                <H>
                                    <meta property='description' content={description || undefined} />
                                    <meta property='og:title' content={title} />
                                    <meta property='og:url' content={`${origin}/${location && location.pathname}`} />
                                    <meta
                                        property='og:image'
                                        content={
                                            (user.profile &&
                                                user.profile &&
                                                user.profile.avatar &&
                                                user.profile.avatar.src) ||
                                            undefined
                                        }
                                    />
                                    <meta property='og:image:width' content='150' />
                                    <meta property='og:image:height' content='150' />
                                </H>
                            ) : null}
                        </React.Fragment>
                    );
                }}
            </FormattedMessage>
        );
    };

    openLikers = () => {
        this.setState({ openLikers: true });
    };

    closeLikers = () => {
        this.setState({ openLikers: false });
    };

    openFriends = () => {
        this.setState({ openFriends: true });
    };

    closeFriends = () => {
        this.setState({ openFriends: false });
    };

    render() {
        const { user, styles, writeMail, onMorePhotoBlogs, onMoreGroups, vendor, userBlog } = this.props;
        const { username, profile, isActive } = user;
        const { aboutMe, name, isVendor, age } = profile || ({} as Partial<NonNullable<AuthorSideFragment['profile']>>);
        const { site } = this.context;

        return (
            <ViewerConsumer>
                {({ viewer }) => (
                    <div className={styles.authorSide}>
                        <Helmet render={this.renderHelmet} />
                        <Cover
                            style={{ container: styles.coverContainer }}
                            {...(userBlog as any)}
                            imageOnly={true}
                            username={username}
                            isVendor={isVendor}
                            writeMail={writeMail}
                            edit={(viewer && viewer.username) === username}
                            vendor={vendor}
                        />
                        <Avatar style={{ avatar: styles.avatar }} user={this.props} />
                        <div className={styles.header}>
                            <div className={styles.profileSummary}>
                                <AuthorName
                                    user={user}
                                    isActive={Boolean(isActive)}
                                    isAuthor={(viewer && viewer.username) === username}
                                    vendor={vendor}
                                />
                                {isVendor && vendor ? (
                                    <VendorSummaryInfo {...vendor} />
                                ) : (
                                    <UserSummaryInfo name={name || undefined} age={age || undefined} />
                                )}
                                <Cover
                                    {...(userBlog as any)}
                                    buttonsOnly={true}
                                    username={username}
                                    isVendor={isVendor}
                                    writeMail={writeMail}
                                    edit={(viewer && viewer.username) === username}
                                    vendor={vendor}
                                />
                                <AuthorSocialStats {...user} />
                            </div>
                            {aboutMe ? <div className={styles.aboutMe}>{aboutMe}</div> : null}
                            {isVendor ? <VendorInformation vendor={vendor} /> : <UserInformation {...user} />}
                        </div>
                        {!this.state.expanded ? (
                            <button
                                className={styles.additionalInfoButton}
                                onClick={() => {
                                    this.setState({ expanded: !this.state.expanded });
                                }}
                            >
                                {viewer && viewer.username === username ? (
                                    <FormattedMessage id='SHOW_MORE_MY_INFO' />
                                ) : (
                                    <FormattedMessage id='SHOW_MORE_INFO' />
                                )}
                            </button>
                        ) : null}
                        <div
                            className={classnames(styles.additionalInfo, {
                                [styles.additionalInfoShow]: this.state.expanded,
                            })}
                        >
                            <AuthorProject {...user} />
                            <AuthorActivity {...user} onMoreGroups={onMoreGroups} />
                            <AuthorLikedPhotoblogs {...user} onMorePhotoBlogs={onMorePhotoBlogs} />
                            <AuthorCollections {...user} />
                            {site.isSlovak && site.isWedding ? (
                                <ZoznamHalfpageTop
                                    sizeMapping={[
                                        [[0, 0], []],
                                        [
                                            [1024, 0],
                                            ['fluid', [300, 600], [300, 300]],
                                        ],
                                    ]}
                                />
                            ) : (
                                <Halfpage
                                    sizeMapping={[
                                        [[0, 0], []],
                                        [
                                            [1024, 0],
                                            ['fluid', [300, 600], [300, 300]],
                                        ],
                                    ]}
                                    adUnitCode='halfpage-top'
                                />
                            )}
                        </div>
                    </div>
                )}
            </ViewerConsumer>
        );
    }
}

export default Styled(defaultStyles)(AuthorSide as any);
